import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { ChevronLeft, ChevronRight, Download, Users } from 'lucide-react';
import { api, hlaskaZChyby } from '../api/client.js';
import type { DochazkaZaznam, Zakazka } from '../api/types.js';
import { useVybranaZakazka } from '../lib/globalniZakazka.js';
import { Btn, Chyba, EmptyState, Karta, Nacitani, Napoveda } from '../components/ui.js';

/**
 * Docházka (kancelář) — měsíční tabulka osoby × dny pro kontrolu před mzdami
 * a JEDEN výstup: Excel pro OKbase (konec dvojího zápisu papír → OKbase).
 * Zapisuje mistr v terénu; tady se kontroluje a exportuje. Žádné mzdy —
 * jen hodiny (invariant: individuální mzdy do systému nikdy).
 */

const MESICE = [
  'leden',
  'únor',
  'březen',
  'duben',
  'květen',
  'červen',
  'červenec',
  'srpen',
  'září',
  'říjen',
  'listopad',
  'prosinec',
];

export function DochazkaPrehledPage() {
  const dnes = new Date();
  const [rok, setRok] = useState(dnes.getFullYear());
  const [mesic, setMesic] = useState(dnes.getMonth()); // 0–11

  const { data: zakazky } = useQuery({
    queryKey: ['zakazky'],
    queryFn: () => api.get<Zakazka[]>('/api/projects'),
  });
  const { vybrana, vybranaId: zakazkaId } = useVybranaZakazka(zakazky);

  const mesicKlic = `${rok}-${String(mesic + 1).padStart(2, '0')}`;
  const dniVMesici = new Date(rok, mesic + 1, 0).getDate();
  const od = `${mesicKlic}-01`;
  const doDne = `${mesicKlic}-${String(dniVMesici).padStart(2, '0')}`;

  const { data, isLoading, error } = useQuery({
    queryKey: ['dochazka-mesic', zakazkaId, mesicKlic],
    queryFn: () =>
      api.get<DochazkaZaznam[]>(`/api/dochazka?zakazkaId=${zakazkaId}&od=${od}&do=${doDne}`),
    enabled: !!zakazkaId,
  });

  // Mřížka: osoba → {den → hodiny}; Σ po osobách i za měsíc.
  const mrizka = useMemo(() => {
    const osoby = new Map<
      string,
      { jmeno: string; profese: string; dny: Map<number, number>; celkem: number }
    >();
    for (const z of data ?? []) {
      const den = Number(z.datum.slice(8, 10));
      const hodin = Number(z.odpracovanoHodin ?? 0);
      const o = osoby.get(z.osoba.id) ?? {
        jmeno: z.osoba.jmeno,
        profese: z.osoba.profese,
        dny: new Map<number, number>(),
        celkem: 0,
      };
      o.dny.set(den, (o.dny.get(den) ?? 0) + hodin);
      o.celkem += hodin;
      osoby.set(z.osoba.id, o);
    }
    return [...osoby.values()].sort((a, b) => a.jmeno.localeCompare(b.jmeno, 'cs'));
  }, [data]);

  const posun = (smer: number) => {
    const d = new Date(rok, mesic + smer, 1);
    setRok(d.getFullYear());
    setMesic(d.getMonth());
  };

  const fmtHodiny = (n: number) => (Number.isInteger(n) ? String(n) : n.toFixed(2));

  if (!zakazkaId && !zakazky) return <Nacitani text="Načítám zakázky…" />;

  return (
    <div className="contents">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold">Docházka{vybrana ? ` — ${vybrana.nazev}` : ''}</h1>
          <p className="text-base text-text-secondary">
            Hodiny party z terénu — kontrola před mzdami.{' '}
            <Napoveda titulek="Jak docházka funguje">
              Zapisuje mistr v terénu za celou partu. Tady se docházka kontroluje a stahuje pro
              mzdový systém — výpočet mezd dělá OKbase, ne Helfr.
            </Napoveda>
          </p>
        </div>
        {/* Export celé firmy za měsíc — OKbase chce mzdy dohromady, ne po zakázkách. */}
        <a href={`/api/dochazka/export?mesic=${mesicKlic}`} download>
          <Btn size="desktop">
            <Download size={16} strokeWidth={2} aria-hidden="true" /> Excel pro OKbase
          </Btn>
        </a>
      </div>

      <div className="flex items-center gap-2">
        <button
          type="button"
          aria-label="Předchozí měsíc"
          className="flex size-9 items-center justify-center rounded-md border border-border text-text-secondary hover:bg-bg"
          onClick={() => posun(-1)}
        >
          <ChevronLeft size={16} strokeWidth={2} aria-hidden="true" />
        </button>
        <p className="min-w-36 text-center text-base font-bold">
          {MESICE[mesic]} {rok}
        </p>
        <button
          type="button"
          aria-label="Další měsíc"
          className="flex size-9 items-center justify-center rounded-md border border-border text-text-secondary hover:bg-bg"
          onClick={() => posun(1)}
        >
          <ChevronRight size={16} strokeWidth={2} aria-hidden="true" />
        </button>
      </div>

      {error && <Chyba>{hlaskaZChyby(error)}</Chyba>}
      {isLoading && <Nacitani text="Načítám docházku…" />}

      {data && mrizka.length === 0 && (
        <EmptyState
          ikona={<Users size={28} strokeWidth={1.5} aria-hidden="true" />}
          titulek="V tomto měsíci žádná docházka"
          popis="Docházku zapisuje mistr v terénu (Menu → Docházka party). Jakmile zapíše první den, uvidíte ji tady."
        />
      )}

      {mrizka.length > 0 && (
        <Karta>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr>
                  <th className="sticky left-0 bg-surface px-3 py-2 text-left text-xs font-semibold text-text-secondary">
                    Osoba
                  </th>
                  {Array.from({ length: dniVMesici }, (_, i) => (
                    <th
                      key={i + 1}
                      className="px-1 py-2 text-center text-xs font-semibold text-text-secondary tabular-nums"
                    >
                      {i + 1}
                    </th>
                  ))}
                  <th className="px-3 py-2 text-right text-xs font-semibold text-text-secondary">
                    Σ hodin
                  </th>
                </tr>
              </thead>
              <tbody>
                {mrizka.map((o) => (
                  <tr key={o.jmeno}>
                    <td className="sticky left-0 border-t border-border bg-surface px-3 py-2 text-sm whitespace-nowrap">
                      <span className="font-semibold">{o.jmeno}</span>{' '}
                      <span className="text-text-secondary">({o.profese})</span>
                    </td>
                    {Array.from({ length: dniVMesici }, (_, i) => {
                      const h = o.dny.get(i + 1);
                      return (
                        <td
                          key={i + 1}
                          className={`border-t border-border px-1 py-2 text-center text-sm tabular-nums ${
                            h ? 'font-semibold' : 'text-text-muted'
                          }`}
                        >
                          {h ? fmtHodiny(h) : '·'}
                        </td>
                      );
                    })}
                    <td className="border-t border-border px-3 py-2 text-right text-sm font-bold tabular-nums">
                      {fmtHodiny(o.celkem)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Karta>
      )}
    </div>
  );
}
